/**
 * The whole state of the survey builder.
 *
 * Every language lives here at once — the tab only decides which slice the fields read and write,
 * so switching tabs never loses what was typed. The identifiers (`key`, option `value`) sit
 * outside the language slices, because they are one code for the three languages and are written
 * into every answer row (R5).
 *
 * Saving is two calls, in this order: the shell (`POST`/`PATCH`) and then the questionnaire
 * (`PUT /:id/questions`). The split is the BFF's, and it is what lets metadata stay editable on a
 * survey with answers while the questionnaire refuses and asks for a new version (R1).
 *
 * @module Pages/BUs/Surveys
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApproveSurvey } from './useApproveSurvey';
import { useBumpSurveyVersion } from './useBumpSurveyVersion';
import { useCreateSurvey } from './useCreateSurvey';
import { useRejectSurvey } from './useRejectSurvey';
import { useSaveSurveyQuestions } from './useSaveSurveyQuestions';
import { useSubmitSurvey } from './useSubmitSurvey';
import { useSurveyResponses } from './useSurveyResponses';
import { useUpdateSurvey } from './useUpdateSurvey';
import {
  SurveyMissingTranslationsError,
  SurveySlugHeldByDeletedError,
  SurveyVersionLockedError,
} from '../Services/Surveys.errors';
import type {
  MatrixRowType,
  SurveyDetail,
  SurveyLanguage,
  SurveyQuestionType,
  SurveyTrigger,
} from '../Types/Surveys.types';
import type { QuestionnaireContent, QuestionnaireProblem } from '../Utils/questionnaireJson';
import {
  getQuestionnaireProblems,
  parseQuestionnaire,
  serializeQuestionnaire,
} from '../Utils/questionnaireJson';
import type {
  QuestionConfigNumbers,
  QuestionConfigTexts,
  SurveyFormData,
  SurveyFormErrors,
  SurveyOptionDraft,
  SurveyQuestionDraft,
  SurveyRowDraft,
  SurveyTextsDraft,
  UseCaseConflict,
} from '../Utils/surveyForm';
import {
  BASE_LANGUAGE,
  buildCreatePayload,
  buildQuestionsPayload,
  buildUpdatePayload,
  createEmptyFormData,
  createEmptyOption,
  createEmptyQuestion,
  createEmptyRow,
  defaultNumbersFor,
  hasErrors,
  mapSurveyToFormData,
  slugifyCode,
  slugifySlug,
  takenKeys,
  uniqueCode,
  validateFormData,
} from '../Utils/surveyForm';
import { savingOpensNextVersion } from '../Utils/surveyFormat';
import { useActionToast } from '@/Hooks/useActionToast';

interface UseSurveyBuilderFormParams {
  /** The survey being edited, or `undefined` for a new one. */
  survey?: SurveyDetail;
  /** Called with the survey id once a creation lands, so the route can become the edit route. */
  onCreated: (id: string) => void;
  /**
   * An extra reason to refuse saving, computed from data this hook does not hold.
   *
   * Today it is the empty intersection between the survey's use case and the one the opt-in
   * feature is offered to — a survey that reaches nobody and says nothing about it. The catalogue
   * of features lives in the screen, so the check is passed in rather than imported here.
   */
  findTargetingConflict?: (form: SurveyFormData) => UseCaseConflict | null;
  /**
   * What a **new** survey starts filled in with, on top of the empty form.
   *
   * Only read on the first render, and ignored entirely when a survey is being edited. It exists
   * for one entry point: "Criar questionário de saída" on an opt-in feature, which knows the BU and
   * the feature and should not make somebody pick them again — picking the wrong one there is how a
   * questionnaire ends up attached to the feature next to the one it was written for.
   */
  initialDraft?: Partial<SurveyFormData>;
}

/** What the version dialog needs to state the consequence of each choice. */
export interface VersionPrompt {
  responseCount: number;
}

export interface UseSurveyBuilderForm {
  form: SurveyFormData;
  language: SurveyLanguage;
  setLanguage: (language: SurveyLanguage) => void;
  isEditing: boolean;
  isSaving: boolean;
  errors: SurveyFormErrors;
  responseCount: number;

  setField: <K extends keyof SurveyFormData>(field: K, value: SurveyFormData[K]) => void;
  /**
   * The trigger, which is not a field like the others.
   *
   * Choosing `ON_OPT_OUT` **is** choosing a rule: the questionnaire hangs off one opt-in feature and
   * is asked to whoever is turning it off. Leaving the eligibility fields to be set separately would
   * let somebody save an exit questionnaire conditioned on `ENABLED`, which is the opposite of when
   * it is asked, or on nothing at all, which the BFF answers `400` to. So the pair moves together.
   */
  setTrigger: (trigger: SurveyTrigger) => void;
  setSlug: (value: string) => void;
  setSurveyText: (field: keyof SurveyTextsDraft, value: string) => void;

  addQuestion: (step: number) => void;
  removeQuestion: (uid: string) => void;
  duplicateQuestion: (uid: string) => void;
  /** Drag-and-drop lands here: the question goes to `step`, at `index` inside it. */
  moveQuestion: (uid: string, step: number, index: number) => void;
  addStep: () => void;
  setQuestionType: (uid: string, type: SurveyQuestionType) => void;
  setQuestionKey: (uid: string, value: string) => void;
  setQuestionLabel: (uid: string, value: string) => void;
  setQuestionHelpText: (uid: string, value: string) => void;
  setQuestionRequired: (uid: string, value: boolean) => void;
  setQuestionNumber: (uid: string, field: keyof QuestionConfigNumbers, value: string) => void;
  setQuestionConfigText: (uid: string, field: keyof QuestionConfigTexts, value: string) => void;

  addOption: (uid: string) => void;
  removeOption: (uid: string, optionUid: string) => void;
  moveOption: (uid: string, optionUid: string, direction: -1 | 1) => void;
  setOptionValue: (uid: string, optionUid: string, value: string) => void;
  setOptionLabel: (uid: string, optionUid: string, value: string) => void;
  setOptionFlag: (
    uid: string,
    optionUid: string,
    flag: 'isExclusive' | 'allowsFreeText',
    value: boolean,
  ) => void;

  addRow: (uid: string) => void;
  removeRow: (uid: string, rowUid: string) => void;
  setRowKey: (uid: string, rowUid: string, value: string) => void;
  setRowLabel: (uid: string, rowUid: string, value: string) => void;
  setRowType: (uid: string, rowUid: string, type: MatrixRowType) => void;
  setRowRequired: (uid: string, rowUid: string, value: boolean) => void;

  /**
   * Everything still missing, across all three languages. Blocks the save button (R6b).
   *
   * Each entry says where it lives, so a collapsed step can announce it and the panel can open the
   * step and put the cursor in the field.
   */
  problems: QuestionnaireProblem[];
  /** The survey's use case and the opt-in feature's do not overlap, so nobody is reachable. */
  targetingConflict: UseCaseConflict | null;
  canSave: boolean;

  /**
   * The `409` of a slug held by a **deleted** survey, or `null`.
   *
   * It is not shown as a failure: the screen offers to restore that survey, because telling
   * somebody the slug is taken by a row they cannot see, recreate or reach is the dead end this
   * exists to close.
   */
  slugHeldByDeleted: SurveySlugHeldByDeletedError | null;
  dismissSlugConflict: () => void;

  /** The same content as a JSON document: the other view, kept in sync both ways. */
  json: string;
  jsonError: string | null;
  setJson: (raw: string) => void;

  save: () => void;
  /** Open while the BFF refused a structural edit and a version decision is owed. */
  versionPrompt: VersionPrompt | null;
  dismissVersionPrompt: () => void;
  resolveVersion: (reask: boolean) => void;
  isResolvingVersion: boolean;

  /**
   * Open while a save on an approved survey is about to open the next version (R4).
   *
   * Carries the version that stays on the air, which is what the dialog has to name — the one being
   * opened is that plus one.
   */
  draftVersionPrompt: { liveVersion: number } | null;
  confirmDraftVersion: () => void;
  dismissDraftVersionPrompt: () => void;

  /**
   * Repopulates the form from the server, for the operations that move the survey under it.
   *
   * The form seeds once on purpose — a refetch after a save must not overwrite what is on screen.
   * `discard-draft` and `reset` break that assumption: both change which version the questionnaire
   * belongs to, so what is in the fields stopped being the survey. Called with the fresh survey it
   * seeds immediately; called with nothing it forgets the seed, so the next payload to arrive
   * repopulates.
   */
  reseedFromServer: (fresh?: SurveyDetail) => void;

  submitForApproval: () => void;
  approve: () => void;
  reject: (reason: string) => void;
  isModerating: boolean;
  /** Languages the BFF refused to approve without. Cleared on the next attempt. */
  missingLanguages: string[] | null;
  dismissMissingLanguages: () => void;
}

export const useSurveyBuilderForm = ({
  survey,
  onCreated,
  findTargetingConflict,
  initialDraft,
}: UseSurveyBuilderFormParams): UseSurveyBuilderForm => {
  const toast = useActionToast();

  const [form, setForm] = useState<SurveyFormData>(() =>
    survey ? mapSurveyToFormData(survey) : { ...createEmptyFormData(), ...initialDraft },
  );
  const [language, setLanguage] = useState<SurveyLanguage>(BASE_LANGUAGE);
  const [errors, setErrors] = useState<SurveyFormErrors>({});
  /** Set when create answered `409` naming a deleted survey. `null` the rest of the time. */
  const [slugHeldByDeleted, setSlugHeldByDeleted] = useState<SurveySlugHeldByDeletedError | null>(
    null,
  );
  const [versionPrompt, setVersionPrompt] = useState<VersionPrompt | null>(null);
  const [draftVersionPrompt, setDraftVersionPrompt] = useState<{ liveVersion: number } | null>(
    null,
  );
  const [missingLanguages, setMissingLanguages] = useState<string[] | null>(null);

  const createSurvey = useCreateSurvey();
  const updateSurvey = useUpdateSurvey();
  const saveQuestions = useSaveSurveyQuestions();
  const bumpVersion = useBumpSurveyVersion();
  const submitSurvey = useSubmitSurvey();
  const approveSurvey = useApproveSurvey();
  const rejectSurvey = useRejectSurvey();

  /**
   * Seeds the form when the survey arrives, and never again.
   *
   * The edit route mounts the builder before the request answers, so the first payload has to
   * land — but a refetch (after a save, after a bump) must not overwrite what is on screen.
   */
  const seededId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!survey || seededId.current === survey.id) return;

    seededId.current = survey.id;
    setForm(mapSurveyToFormData(survey));
  }, [survey]);

  const reseedFromServer = useCallback((fresh?: SurveyDetail) => {
    if (!fresh) {
      seededId.current = undefined;

      return;
    }

    seededId.current = fresh.id;
    setForm(mapSurveyToFormData(fresh));
  }, []);

  const responseCount = survey?.responseCount ?? 0;

  /**
   * How many answers the version under edit has — the number R4's warning depends on.
   *
   * Only asked when it can actually change the answer: the survey is approved, no draft is open,
   * and it has answers **somewhere**. With no answers at all the version has none either, and with
   * a draft open or an unapproved survey no draft is opening regardless — so the common case costs
   * no request. `limit: 1` because only `total` is read.
   */
  const needsVersionCount =
    survey !== undefined &&
    survey.approvalStatus === 'APPROVED' &&
    survey.draftVersion === null &&
    survey.responseCount > 0;

  const { data: versionResponses } = useSurveyResponses(
    survey?.id,
    { version: survey?.workingVersion, limit: 1 },
    needsVersionCount,
  );

  /** `null` while unknown — in flight or failed. {@link savingOpensNextVersion} warns on unknown. */
  const versionResponseCount = versionResponses?.total ?? null;

  const setField = useCallback(
    <K extends keyof SurveyFormData>(field: K, value: SurveyFormData[K]) => {
      setForm(current => ({ ...current, [field]: value }));
    },
    [],
  );

  const setTrigger = useCallback((trigger: SurveyTrigger) => {
    setForm(current =>
      trigger === 'ON_OPT_OUT'
        ? {
            ...current,
            trigger,
            conditionOnOptIn: true,
            // The state it is asked in, by definition: the person is leaving as they answer.
            requiredOptInState: 'DISABLED',
          }
        : { ...current, trigger },
    );
  }, []);

  const setSlug = useCallback((value: string) => {
    setForm(current => ({ ...current, slug: slugifySlug(value) }));
  }, []);

  const setSurveyText = useCallback(
    (field: keyof SurveyTextsDraft, value: string) => {
      setForm(current => ({
        ...current,
        texts: { ...current.texts, [language]: { ...current.texts[language], [field]: value } },
      }));
    },
    [language],
  );

  /* ─────────────────────────────── Questions ────────────────────────────── */

  const mapQuestion = useCallback(
    (uid: string, update: (question: SurveyQuestionDraft) => SurveyQuestionDraft) => {
      setForm(current => ({
        ...current,
        questions: current.questions.map(question =>
          question.uid === uid ? update(question) : question,
        ),
      }));
    },
    [],
  );

  const addQuestion = useCallback((step: number) => {
    setForm(current => ({
      ...current,
      questions: [...current.questions, createEmptyQuestion(step)],
    }));
  }, []);

  const addStep = useCallback(() => {
    setForm(current => {
      const highest = current.questions.reduce((max, question) => Math.max(max, question.step), 0);

      return { ...current, questions: [...current.questions, createEmptyQuestion(highest + 1)] };
    });
  }, []);

  const removeQuestion = useCallback((uid: string) => {
    setForm(current => ({
      ...current,
      questions: current.questions.filter(question => question.uid !== uid),
    }));
  }, []);

  /**
   * A copy, with a fresh identity.
   *
   * The clone drops `id` and gets a new `key`: reusing either would tell the BFF this is the same
   * question the answers point at, which is exactly what R5 exists to prevent.
   */
  const duplicateQuestion = useCallback((uid: string) => {
    setForm(current => {
      const source = current.questions.find(question => question.uid === uid);
      if (!source) return current;

      const copy: SurveyQuestionDraft = {
        ...structuredClone(source),
        uid: `${uid}_copy_${Date.now()}`,
        id: undefined,
        key: uniqueCode(source.key || source.labels[BASE_LANGUAGE], takenKeys(current.questions)),
        options: source.options.map(option => ({
          ...structuredClone(option),
          uid: `${option.uid}_copy_${Date.now()}`,
        })),
        rows: source.rows.map(row => ({
          ...structuredClone(row),
          uid: `${row.uid}_copy_${Date.now()}`,
          id: undefined,
        })),
      };

      const at = current.questions.findIndex(question => question.uid === uid);

      return {
        ...current,
        questions: [
          ...current.questions.slice(0, at + 1),
          copy,
          ...current.questions.slice(at + 1),
        ],
      };
    });
  }, []);

  /**
   * Moves a question to a position inside a step.
   *
   * `order` is not stored — it is recomputed from the array on save — so a move is a splice and
   * nothing else. Two questions can never claim the same order because no one holds one.
   */
  const moveQuestion = useCallback((uid: string, step: number, index: number) => {
    setForm(current => {
      const moving = current.questions.find(question => question.uid === uid);
      if (!moving) return current;

      const remaining = current.questions.filter(question => question.uid !== uid);
      const target = { ...moving, step };
      const inStep = remaining.filter(question => question.step === step);
      const before = inStep.slice(0, index);
      const after = inStep.slice(index);
      const others = remaining.filter(question => question.step !== step);

      // The array stays grouped by step so the editor can render it straight through.
      const rebuilt = [...others, ...before, target, ...after].sort((a, b) => a.step - b.step);

      return { ...current, questions: rebuilt };
    });
  }, []);

  /**
   * Changing the type resets what no longer applies.
   *
   * Keeping a rating's `min`/`max` on a question that became a text box would send config the
   * renderer ignores and the results service would try to build a scale from.
   */
  const setQuestionType = useCallback(
    (uid: string, type: SurveyQuestionType) => {
      mapQuestion(uid, question => ({
        ...question,
        type,
        numbers: defaultNumbersFor(type),
        options:
          type === 'SINGLE_CHOICE' || type === 'MULTI_CHOICE' || type === 'MATRIX'
            ? question.options.length > 0
              ? question.options
              : [createEmptyOption(), createEmptyOption()]
            : [],
        rows:
          type === 'MATRIX' ? (question.rows.length > 0 ? question.rows : [createEmptyRow()]) : [],
      }));
    },
    [mapQuestion],
  );

  const setQuestionKey = useCallback(
    (uid: string, value: string) => {
      mapQuestion(uid, question => ({
        ...question,
        key: slugifyCode(value),
        // Typed by hand: from here on it stops following the label, or every typo fix would
        // rewrite the CSV column name under a questionnaire someone already reviewed.
        keyTouched: true,
      }));
    },
    [mapQuestion],
  );

  /**
   * The label, and the key that follows it while nobody has taken the key over.
   *
   * Only the base language drives the key: deriving it from whichever tab is open would give the
   * same question a different column name depending on where it was typed.
   */
  const setQuestionLabel = useCallback(
    (uid: string, value: string) => {
      setForm(current => ({
        ...current,
        questions: current.questions.map(question => {
          if (question.uid !== uid) return question;

          const labels = { ...question.labels, [language]: value };
          const shouldFollow = !question.keyTouched && language === BASE_LANGUAGE;

          return {
            ...question,
            labels,
            key: shouldFollow ? uniqueCode(value, takenKeys(current.questions, uid)) : question.key,
          };
        }),
      }));
    },
    [language],
  );

  const setQuestionHelpText = useCallback(
    (uid: string, value: string) => {
      mapQuestion(uid, question => ({
        ...question,
        helpTexts: { ...question.helpTexts, [language]: value },
      }));
    },
    [language, mapQuestion],
  );

  const setQuestionRequired = useCallback(
    (uid: string, value: boolean) => {
      mapQuestion(uid, question => ({ ...question, isRequired: value }));
    },
    [mapQuestion],
  );

  const setQuestionNumber = useCallback(
    (uid: string, field: keyof QuestionConfigNumbers, value: string) => {
      mapQuestion(uid, question => {
        const parsed = value.trim() === '' ? undefined : Number(value);
        const numbers = { ...question.numbers };

        if (parsed === undefined || !Number.isFinite(parsed)) {
          delete numbers[field];
        } else {
          numbers[field] = Math.trunc(parsed);
        }

        return { ...question, numbers };
      });
    },
    [mapQuestion],
  );

  const setQuestionConfigText = useCallback(
    (uid: string, field: keyof QuestionConfigTexts, value: string) => {
      mapQuestion(uid, question => ({
        ...question,
        configTexts: {
          ...question.configTexts,
          [language]: { ...question.configTexts[language], [field]: value },
        },
      }));
    },
    [language, mapQuestion],
  );

  /* ──────────────────────────────── Options ─────────────────────────────── */

  const mapOption = useCallback(
    (uid: string, optionUid: string, update: (option: SurveyOptionDraft) => SurveyOptionDraft) => {
      mapQuestion(uid, question => ({
        ...question,
        options: question.options.map(option =>
          option.uid === optionUid ? update(option) : option,
        ),
      }));
    },
    [mapQuestion],
  );

  const addOption = useCallback(
    (uid: string) => {
      mapQuestion(uid, question => ({
        ...question,
        options: [...question.options, createEmptyOption()],
      }));
    },
    [mapQuestion],
  );

  /**
   * Removing an option does **not** remove the answers behind it.
   *
   * They keep the `value` that was stored, so the results screen goes on counting them under a
   * code that no longer has a label. The editor says so before removing.
   */
  const removeOption = useCallback(
    (uid: string, optionUid: string) => {
      mapQuestion(uid, question => ({
        ...question,
        options: question.options.filter(option => option.uid !== optionUid),
      }));
    },
    [mapQuestion],
  );

  const moveOption = useCallback(
    (uid: string, optionUid: string, direction: -1 | 1) => {
      mapQuestion(uid, question => {
        const at = question.options.findIndex(option => option.uid === optionUid);
        const to = at + direction;

        if (at === -1 || to < 0 || to >= question.options.length) return question;

        const options = [...question.options];
        [options[at], options[to]] = [options[to], options[at]];

        return { ...question, options };
      });
    },
    [mapQuestion],
  );

  const setOptionValue = useCallback(
    (uid: string, optionUid: string, value: string) => {
      mapOption(uid, optionUid, option => ({ ...option, value: slugifyCode(value) }));
    },
    [mapOption],
  );

  /**
   * The option's label, and the code that follows it while the code is still blank.
   *
   * Unlike the question key there is no "touched" flag: an option's code is short and the first
   * label is almost always the right seed, so it follows only while empty.
   */
  const setOptionLabel = useCallback(
    (uid: string, optionUid: string, value: string) => {
      mapOption(uid, optionUid, option => ({
        ...option,
        labels: { ...option.labels, [language]: value },
        value:
          option.value === '' && language === BASE_LANGUAGE ? slugifyCode(value) : option.value,
      }));
    },
    [language, mapOption],
  );

  const setOptionFlag = useCallback(
    (uid: string, optionUid: string, flag: 'isExclusive' | 'allowsFreeText', value: boolean) => {
      mapOption(uid, optionUid, option => ({ ...option, [flag]: value }));
    },
    [mapOption],
  );

  /* ───────────────────────────────── Rows ───────────────────────────────── */

  const mapRow = useCallback(
    (uid: string, rowUid: string, update: (row: SurveyRowDraft) => SurveyRowDraft) => {
      mapQuestion(uid, question => ({
        ...question,
        rows: question.rows.map(row => (row.uid === rowUid ? update(row) : row)),
      }));
    },
    [mapQuestion],
  );

  const addRow = useCallback(
    (uid: string) => {
      mapQuestion(uid, question => ({ ...question, rows: [...question.rows, createEmptyRow()] }));
    },
    [mapQuestion],
  );

  const removeRow = useCallback(
    (uid: string, rowUid: string) => {
      mapQuestion(uid, question => ({
        ...question,
        rows: question.rows.filter(row => row.uid !== rowUid),
      }));
    },
    [mapQuestion],
  );

  const setRowKey = useCallback(
    (uid: string, rowUid: string, value: string) => {
      mapRow(uid, rowUid, row => ({ ...row, key: slugifyCode(value), keyTouched: true }));
    },
    [mapRow],
  );

  const setRowLabel = useCallback(
    (uid: string, rowUid: string, value: string) => {
      setForm(current => ({
        ...current,
        questions: current.questions.map(question => {
          if (question.uid !== uid) return question;

          return {
            ...question,
            rows: question.rows.map(row => {
              if (row.uid !== rowUid) return row;

              const shouldFollow = !row.keyTouched && language === BASE_LANGUAGE;

              return {
                ...row,
                labels: { ...row.labels, [language]: value },
                key: shouldFollow
                  ? uniqueCode(value, takenKeys(current.questions, rowUid), 'linha')
                  : row.key,
              };
            }),
          };
        }),
      }));
    },
    [language],
  );

  const setRowType = useCallback(
    (uid: string, rowUid: string, type: MatrixRowType) => {
      mapRow(uid, rowUid, row => ({ ...row, type }));
    },
    [mapRow],
  );

  const setRowRequired = useCallback(
    (uid: string, rowUid: string, value: boolean) => {
      mapRow(uid, rowUid, row => ({ ...row, isRequired: value }));
    },
    [mapRow],
  );

  /* ───────────────────────────── The JSON view ──────────────────────────── */

  const [json, setJsonText] = useState<string>(() =>
    serializeQuestionnaire({ texts: form.texts, questions: form.questions }),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  /** Set while a form change came from the JSON view, so the text is not rewritten mid-typing. */
  const isEditingJson = useRef(false);

  useEffect(() => {
    if (isEditingJson.current) {
      isEditingJson.current = false;

      return;
    }

    setJsonText(serializeQuestionnaire({ texts: form.texts, questions: form.questions }));
    setJsonError(null);
  }, [form.texts, form.questions]);

  const applyContent = useCallback((content: QuestionnaireContent) => {
    setForm(current => ({ ...current, texts: content.texts, questions: content.questions }));
  }, []);

  const setJson = useCallback(
    (raw: string) => {
      setJsonText(raw);

      const result = parseQuestionnaire(raw);

      if (!result.ok) {
        setJsonError(result.message);

        return;
      }

      setJsonError(null);
      isEditingJson.current = true;
      applyContent(result.content);
    },
    [applyContent],
  );

  const problems = useMemo(
    () => getQuestionnaireProblems({ texts: form.texts, questions: form.questions }),
    [form.texts, form.questions],
  );

  const targetingConflict = findTargetingConflict ? findTargetingConflict(form) : null;

  /**
   * An incomplete survey can still be saved **switched off**.
   *
   * Same escape hatch the opt-in cadastro has: off, it is in nobody's delivery queue, so a
   * missing translation reaches no one. On, the pendências panel holds the button (R6b) — and
   * unlike the opt-in feature there is an `approve` behind it too, which is the second net, not
   * the first.
   *
   * A targeting contradiction is held the same way: a survey that reaches nobody should not be
   * saved **active**, and switching it off is the way to park it while the targeting is sorted.
   */
  const canSave = (problems.length === 0 && targetingConflict === null) || !form.isActive;

  /* ─────────────────────────────── Saving ───────────────────────────────── */

  const persistQuestions = useCallback(
    async (id: string) => {
      try {
        await saveQuestions.mutateAsync({ id, payload: buildQuestionsPayload(form) });
        toast.success('Pesquisa salva');
        setVersionPrompt(null);
      } catch (error) {
        if (error instanceof SurveyVersionLockedError) {
          setVersionPrompt({ responseCount: error.responseCount });

          return;
        }

        toast.failure('Não foi possível salvar o questionário', error);
      }
    },
    [form, saveQuestions, toast],
  );

  const persist = useCallback(() => {
    const run = async () => {
      if (survey) {
        await updateSurvey.mutateAsync({ id: survey.id, payload: buildUpdatePayload(form) });
        await persistQuestions(survey.id);

        return;
      }

      const created = await createSurvey.mutateAsync(buildCreatePayload(form));
      await persistQuestions(created.id);
      onCreated(created.id);
    };

    void run().catch((error: unknown) => {
      /*
        The slug being held by a DELETED survey is the one failure with a way out, so it opens the
        dialog that offers restoring that survey. Everything else was already reported by the
        mutation that raised it; this only stops the rejection escaping.
      */
      if (error instanceof SurveySlugHeldByDeletedError) setSlugHeldByDeleted(error);
    });
  }, [createSurvey, form, onCreated, persistQuestions, survey, updateSurvey]);

  const save = useCallback(() => {
    if (!canSave) return;

    const found = validateFormData(form);
    setErrors(found);

    if (hasErrors(found)) return;

    /*
      R4 — on an approved survey this save opens the NEXT version instead of rewriting the one on
      the air, and the screen said nothing about it until here. Asked after the form is validated,
      so the dialog is never the thing standing between somebody and a missing field.
    */
    if (survey && savingOpensNextVersion(survey, versionResponseCount)) {
      setDraftVersionPrompt({ liveVersion: survey.version });

      return;
    }

    persist();
  }, [canSave, form, persist, survey, versionResponseCount]);

  const confirmDraftVersion = useCallback(() => {
    setDraftVersionPrompt(null);
    persist();
  }, [persist]);

  const dismissDraftVersionPrompt = useCallback(() => {
    setDraftVersionPrompt(null);
  }, []);

  /**
   * Answers the version dialog, then retries the save that raised it.
   *
   * The metadata was already written before the refusal — `PATCH` never refuses (R1) — so the
   * retry is the questionnaire alone.
   */
  const resolveVersion = useCallback(
    (reask: boolean) => {
      if (!survey) return;

      const run = async () => {
        await bumpVersion.mutateAsync({ id: survey.id, reask });
        await persistQuestions(survey.id);
      };

      void run().catch(() => {
        // Reported by the mutation.
      });
    },
    [bumpVersion, persistQuestions, survey],
  );

  const dismissVersionPrompt = useCallback(() => {
    setVersionPrompt(null);
  }, []);

  /* ────────────────────────────── Moderation ────────────────────────────── */

  const submitForApproval = useCallback(() => {
    if (!survey) return;

    submitSurvey.mutate({ id: survey.id });
  }, [submitSurvey, survey]);

  const approve = useCallback(() => {
    if (!survey) return;

    setMissingLanguages(null);
    approveSurvey.mutate(survey.id, {
      onError: error => {
        if (error instanceof SurveyMissingTranslationsError) {
          setMissingLanguages(error.missingLanguages);
        }
      },
    });
  }, [approveSurvey, survey]);

  const reject = useCallback(
    (reason: string) => {
      if (!survey) return;

      rejectSurvey.mutate({ id: survey.id, reason });
    },
    [rejectSurvey, survey],
  );

  const dismissMissingLanguages = useCallback(() => {
    setMissingLanguages(null);
  }, []);

  return {
    form,
    language,
    setLanguage,
    isEditing: Boolean(survey),
    isSaving: createSurvey.isPending || updateSurvey.isPending || saveQuestions.isPending,
    errors,
    responseCount,
    setField,
    setTrigger,
    setSlug,
    setSurveyText,
    addQuestion,
    removeQuestion,
    duplicateQuestion,
    moveQuestion,
    addStep,
    setQuestionType,
    setQuestionKey,
    setQuestionLabel,
    setQuestionHelpText,
    setQuestionRequired,
    setQuestionNumber,
    setQuestionConfigText,
    addOption,
    removeOption,
    moveOption,
    setOptionValue,
    setOptionLabel,
    setOptionFlag,
    addRow,
    removeRow,
    setRowKey,
    setRowLabel,
    setRowType,
    setRowRequired,
    problems,
    targetingConflict,
    canSave,
    slugHeldByDeleted,
    dismissSlugConflict: () => {
      setSlugHeldByDeleted(null);
    },
    json,
    jsonError,
    setJson,
    save,
    versionPrompt,
    dismissVersionPrompt,
    resolveVersion,
    isResolvingVersion: bumpVersion.isPending || saveQuestions.isPending,
    draftVersionPrompt,
    confirmDraftVersion,
    dismissDraftVersionPrompt,
    reseedFromServer,
    submitForApproval,
    approve,
    reject,
    isModerating: submitSurvey.isPending || approveSurvey.isPending || rejectSurvey.isPending,
    missingLanguages,
    dismissMissingLanguages,
  };
};
